import { useState } from 'react';
import { formatDuration, formatUKForDisplay, getCounterpartyNumber } from '@marnic/shared';
import { relativeTime } from '../lib/utils';
import { useRedial } from './useRedial';
import type { CallRow } from './useRecentCalls';
import { useAuthStore } from '../store/auth';
import { SaveContactModal } from './SaveContactModal';

interface Props {
  calls: CallRow[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  compact?: boolean;
}

export function RecentCallsList({ calls, selectedId, onSelect, compact = false }: Props): JSX.Element {
  const { redial, busy, canRedial } = useRedial();
  const role = useAuthStore((s) => s.user?.role);
  const canSaveContact = role === 'admin' || role === 'supervisor';
  const [savePhone, setSavePhone] = useState<string | null>(null);

  if (calls.length === 0) {
    return <div className="text-xs text-ink-500 italic px-1 py-2">No recent calls.</div>;
  }

  return (
    <>
      <ul className="divide-y divide-ink-100 rounded-md border border-ink-200 bg-white">
        {calls.map((c) => {
          const counterparty = getCounterpartyNumber(c);
          const name = c.lead ? `${c.lead.firstName} ${c.lead.lastName}` : null;
          const isSelected = c.id === selectedId;
          return (
            <li
              key={c.id}
              className={isSelected ? 'bg-brand-50' : ''}
            >
              <div className="flex items-center gap-2 px-2 py-2">
                <button
                  type="button"
                  onClick={() => onSelect(c.id)}
                  className="flex-1 min-w-0 text-left"
                >
                  <div className="flex items-center gap-2 text-xs">
                    <span aria-hidden>{c.direction === 'outbound' ? '↗' : '↘'}</span>
                    <span className="truncate font-medium">{name ?? formatUKForDisplay(counterparty)}</span>
                    <span className="ml-auto shrink-0 text-ink-500">{relativeTime(c.startedAt)}</span>
                  </div>
                  {!compact && (
                    <div className="mt-0.5 flex items-center gap-2 text-[11px] text-ink-500">
                      {name && <span className="font-mono">{formatUKForDisplay(counterparty)}</span>}
                      <span className="font-mono">{formatDuration(c.durationSeconds ?? 0)}</span>
                      <span className="truncate">{c.disposition ?? c.status}</span>
                      {c.recordingUrl && <span aria-label="Has recording">●</span>}
                    </div>
                  )}
                </button>
                {canSaveContact && !c.lead && (
                  <button
                    type="button"
                    onClick={() => setSavePhone(counterparty)}
                    className="btn-secondary !px-2 !py-1 text-xs"
                    title="Save as contact"
                  >
                    +
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => void redial(c)}
                  disabled={!canRedial || busy}
                  className="btn-secondary !px-2 !py-1 text-xs"
                  title="Redial this number"
                >
                  ↻
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      <SaveContactModal
        phone={savePhone ?? ''}
        open={savePhone !== null}
        onClose={() => setSavePhone(null)}
      />
    </>
  );
}
